import React from 'react';
import { Box, Button, Chip, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import SpeedIcon from '@mui/icons-material/Speed';

const formatMs = (value, unavailable = 'NOT OBSERVED') => {
  if (value == null || !Number.isFinite(Number(value))) return unavailable;
  const ms = Number(value);
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(2)} s`;
};

const diagnosisPresentation = (code) => ({
  healthy: { label: 'HEALTHY', color: '#46d7b0' },
  kairos: { label: 'SERVER TIMING', color: '#ff8f5c' },
  request_path: { label: 'SLOW REQUEST PATH', color: '#5aa9ff' },
  workstation: { label: 'BROWSER STALLS', color: '#b597ff' },
  mixed: { label: 'INSUFFICIENT / MIXED', color: '#ffd166' },
}[code] || { label: 'COLLECTING', color: '#78909c' });

const MiniMetric = ({ label, value, color, unavailable }) => (
  <Box sx={{ minWidth: 124, px: 1.2, py: 0.8, borderLeft: `2px solid ${color}`, bgcolor: `${color}0b`, borderRadius: '0 6px 6px 0' }}>
    <Typography sx={{ color: '#617985', fontSize: 7.5, letterSpacing: 0.9, fontFamily: 'monospace' }}>{label}</Typography>
    <Typography sx={{ mt: 0.2, color, fontSize: 12, fontWeight: 800, fontFamily: 'monospace' }}>{formatMs(value, unavailable)}</Typography>
  </Box>
);

const StudyPerformanceDiagnosticStrip = ({ report, traceId = '', onSubmit, onClose, submitState = 'idle' }) => {
  const diagnosis = diagnosisPresentation(report?.diagnosis?.code);
  const kairosTiming = Number(report?.kairos_ms || 0) > 0 ? report.kairos_ms : null;
  return (
    <Box role="region" aria-label="Study performance check" data-kairos-performance-strip="NO_DIAGNOSTIC_PIXEL_OVERLAY" sx={{
      flex: '0 0 auto', px: 1.2, py: 1, borderBottom: '1px solid rgba(110,239,255,0.16)',
      background: 'linear-gradient(100deg, rgba(5,15,22,0.98), rgba(8,10,15,0.98))',
      boxShadow: 'inset 0 1px rgba(110,239,255,0.04)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
        <SpeedIcon sx={{ fontSize: 17, color: '#6eefff' }} />
        <Box sx={{ minWidth: 160 }}>
          <Typography sx={{ color: '#d7fbff', fontSize: 10, fontWeight: 900, letterSpacing: 1.3, fontFamily: 'monospace' }}>PERFORMANCE CHECK</Typography>
          <Typography sx={{ color: '#56707c', fontSize: 7.5, fontFamily: 'monospace' }}>THIS BROWSER · THIS OPEN · NO PATIENT/STUDY ID</Typography>
        </Box>
        <Chip size="small" label={diagnosis.label} sx={{ height: 22, color: diagnosis.color, bgcolor: `${diagnosis.color}12`, border: `1px solid ${diagnosis.color}55`, fontSize: 8.5, fontWeight: 900, fontFamily: 'monospace' }} />
        {traceId && <Typography sx={{ color: '#5aa9ff', fontSize: 9, fontFamily: 'monospace', fontWeight: 800 }}>TRACE {String(traceId).replace(/-/g, '').slice(0, 8).toUpperCase()}</Typography>}
        <MiniMetric label="FIRST DIAGNOSTIC PIXELS" value={report?.first_image_ms} color="#6eefff" unavailable="NO PIXELS" />
        <MiniMetric label="METADATA READY" value={report?.metadata_ms} color="#46d7b0" />
        <MiniMetric label="SLOWEST REQUEST PATH" value={report?.request_path_ms} color="#5aa9ff" />
        <MiniMetric label="KAIROS SERVER-TIMING" value={kairosTiming} color="#ff8f5c" unavailable="NOT EXPOSED" />
        <MiniMetric label="BROWSER MAIN-THREAD STALLS" value={report?.long_task_ms} color="#b597ff" />
        <Box sx={{ minWidth: 220, flex: '1 1 260px' }}>
          <Typography sx={{ color: '#647b86', fontSize: 7.5, lineHeight: 1.45, fontFamily: 'monospace' }}>
            {report?.diagnosis?.summary || 'Collecting accepted-pixel and bounded browser evidence.'} Request-path timing does not separate Kairos from transport unless server-owned timing is exposed.
          </Typography>
          <Typography sx={{ mt: 0.35, color: '#526b76', fontSize: 7.5, fontFamily: 'monospace' }}>
            {String(report?.outcome || 'collecting').toUpperCase()} · {report?.painted_viewport_count || 0}/{report?.viewport_count || 0} VIEWPORTS ACCEPTED
          </Typography>
        </Box>
        <Button size="small" variant="outlined" disabled={!report || submitState === 'sending' || submitState === 'sent'} onClick={onSubmit} aria-label="Send anonymized performance sample"
          startIcon={<CloudUploadOutlinedIcon sx={{ fontSize: 14 }} />} sx={{ minHeight: 26, color: submitState === 'sent' ? '#46d7b0' : '#6eefff', borderColor: submitState === 'sent' ? 'rgba(70,215,176,0.4)' : 'rgba(110,239,255,0.28)', fontFamily: 'monospace', fontSize: 8.5 }}>
          {submitState === 'sending' ? 'SENDING…' : submitState === 'sent' ? 'SAMPLE SENT' : submitState === 'error' ? 'RETRY SAMPLE' : 'SEND SAMPLE'}
        </Button>
        <Tooltip title="Close performance check"><IconButton size="small" onClick={onClose} aria-label="Close performance check" sx={{ color: '#58717c' }}><CloseIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
      </Box>
      {submitState === 'sending' && <LinearProgress sx={{ mt: 0.8, height: 2, bgcolor: '#0b1116', '& .MuiLinearProgress-bar': { bgcolor: '#6eefff' } }} />}
    </Box>
  );
};

export default StudyPerformanceDiagnosticStrip;
